import { limit, where } from 'firebase/firestore'
import { FirestoreRepository } from '@upadelmatch/core'
import { MatchInviteSchema, inviteDocId, type MatchInviteDoc } from '@/core/models/match-invite'
import type { MatchView } from '../use-matches'

// `match_invites` — organizer → player invitations (spec/02). Rules verify
// the sender IS the match organizer and limit the invitee to a status flip.
// The push CF (on_match_invite_written) fires on create only.

/** Bound on one player's invite history — well past any real Actividad list. */
export const INVITES_PAGE_SIZE = 300

/** Denormalized copy the push + strip render without a match read — the
 *  same display strings the player would see on the card. */
function inviteSummary(match: MatchView) {
  return {
    place: match.place || null,
    day: match.dayLabel || null,
    time: match.timeLabel || null,
  }
}

class MatchInvitesRepository extends FirestoreRepository<typeof MatchInviteSchema> {
  constructor() {
    super('match_invites', MatchInviteSchema)
  }

  /** My pending inbox (the Partidos strip). */
  listPendingFor(uid: string): Promise<MatchInviteDoc[]> {
    return this.list(where('toUid', '==', uid), where('status', '==', 'pending'), limit(INVITES_PAGE_SIZE))
  }

  /**
   * Full history for the Actividad screen (client-sorted).
   *
   * Bounded: "small N" was true on day one and stops being true for anyone who
   * plays weekly — `match_invites` never shrinks, and this read is on the path
   * of a screen reachable from the feed's bell.
   */
  listAllFor(uid: string, max: number = INVITES_PAGE_SIZE): Promise<MatchInviteDoc[]> {
    return this.list(where('toUid', '==', uid), limit(max))
  }

  /** Everyone already invited to a match by me (picker "Invitado" state). */
  listSentForMatch(fromUid: string, matchId: string): Promise<MatchInviteDoc[]> {
    return this.list(where('fromUid', '==', fromUid), where('matchId', '==', matchId))
  }

  /** Batch-send — idempotent per invitee (deterministic doc id). */
  async sendAll(
    match: MatchView,
    fromUid: string,
    fromName: string,
    toUids: string[],
  ): Promise<void> {
    const summary = inviteSummary(match)
    await Promise.all(
      toUids.map((toUid) =>
        this.set(
          inviteDocId(match.id, toUid),
          { matchId: match.id, fromUid, fromName, toUid, status: 'pending', ...summary },
          { setCreatedAt: true },
        ),
      ),
    )
  }

  respond(matchId: string, toUid: string, status: 'accepted' | 'declined'): Promise<void> {
    return this.update(inviteDocId(matchId, toUid), { status })
  }
}

export const matchInvitesRepository = new MatchInvitesRepository()
